// src/onboarding/OnboardingWizard.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: orchestrator. Renders the wizard scenes in sequence (Welcome →
// GardenSpaces → Areas → per-area SubSpaces → Plant → TaskReveal). Owns the
// dynamic step list, forward/back navigation, and the dialog chrome (close-X,
// Back button, Skip link).

import { useEffect, useMemo } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import OnboardingWelcome from './OnboardingWelcome.jsx';
import OnboardingGardenSpaces from './OnboardingGardenSpaces.jsx';
import OnboardingAreas from './OnboardingAreas.jsx';
import OnboardingSubSpaces from './OnboardingSubSpaces.jsx';
import OnboardingPlant from './OnboardingPlant.jsx';
import OnboardingTaskReveal from './OnboardingTaskReveal.jsx';
import {
  ONBOARD_PRESET_AREAS,
  GARDEN_SPACE_TO_AREA_LABEL, GARDEN_SPACE_TO_CUSTOM_AREA,
} from './constants.js';
import { logEvent } from '../data/events.js';

export default function OnboardingWizard({step, setStep, onDismiss, onComplete, onboardData, setOnboardData, areas, setAreas, plants, newPlant, setNewPlant, onAddPlant, features, aiReady, aiStatus = 'ready', onAiFailure, setView}) {
  // Dynamic step list — later plans (03–05) add gardenSpaces and subspaces entries here
  // without further router changes. Counter and routing both derive from steps.length.
  //
  // Iterating the full `areas` array leaked unticked INITIAL_AREAS (Back Garden +
  // Front Garden are seeded on first run regardless of user selection) and any
  // preset appended during a prior forward pass that the user later unticked via
  // back-navigation — SubSpaces then prompted for spaces the user never chose.
  // Filter to the preset names corresponding to the user's Step 1 gardenSpaces
  // picks, plus any non-preset custom areas (preserves re-run user customs).
  const subSpaceAreaNames = useMemo(() => {
    const ticked = Array.isArray(onboardData?.gardenSpaces) ? onboardData.gardenSpaces : [];
    if (ticked.length === 0) return (areas || []).map(a => a.name);
    const tickedPresetNames = new Set(
      ticked
        .map(v => GARDEN_SPACE_TO_AREA_LABEL[v] || GARDEN_SPACE_TO_CUSTOM_AREA[v])
        .filter(Boolean)
        .map(n => n.toLowerCase())
    );
    const allPresetNames = new Set([
      ...ONBOARD_PRESET_AREAS.map(n => n.toLowerCase()),
      ...Object.values(GARDEN_SPACE_TO_CUSTOM_AREA).map(n => n.toLowerCase()),
    ]);
    return (areas || [])
      .filter(a => {
        const lower = String(a.name || "").toLowerCase();
        if (tickedPresetNames.has(lower)) return true;
        return !allPresetNames.has(lower);
      })
      .map(a => a.name);
  }, [areas, onboardData?.gardenSpaces]);
  const steps = useMemo(() => [
    { key: "welcome" },
    { key: "gardenSpaces" },
    { key: "areas" },
    ...subSpaceAreaNames.map(name => ({ key: `sub:${name}` })),
    { key: "plant" },
    { key: "reveal" },
  ], [subSpaceAreaNames]);
  const totalSteps = steps.length;
  const safeStep = Math.min(Math.max(step, 0), totalSteps - 1);
  const currentKey = steps[safeStep].key;
  const goNext = () => {
    // Phase 32 D-01 — onboarding_step fires on FORWARD transition only (goBack stays silent).
    // Sub-space step keys are `sub:<area-name>`; <area-name> can be a user-typed custom area
    // (PII risk under v3.0 Supabase flush). Collapse all sub-space variants to a single enum.
    const logKey = currentKey.startsWith('sub:') ? 'sub_space' : currentKey;
    logEvent('onboarding_step', { step: logKey, index: safeStep });
    setStep(safeStep + 1);
  };
  const goBack = () => setStep(Math.max(safeStep - 1, 0));

  // M-02: write the clamped value back to the parent so step never desyncs from safeStep
  useEffect(() => {
    if (step !== safeStep) setStep(safeStep);
  }, [step, safeStep, setStep]);

  return (
    <div
      role="dialog"
      aria-label="First run setup"
      style={{position:"fixed", inset:0, background:"var(--c-page)", zIndex:300, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:24, overflowY:"auto", WebkitOverflowScrolling:"touch"}}
    >
      <div style={{maxWidth:520, width:"100%", background:"var(--c-bg)", borderRadius:18, padding:0, boxShadow:"0 8px 40px rgba(0,0,0,0.2)", position:"relative"}}>
        {/* Phase 31.3 Plan 06 Task 1 Step 3b — close-X relocated INSIDE the card
            so it anchors to the card (card is position:relative) rather than
            the outer dialog. UAT-Gap-2 inline style contract preserved verbatim:
            borderRadius:8, lineHeight:1, onClick={onDismiss}. */}
        <button
          aria-label="Close setup"
          onClick={onDismiss}
          style={{position:"absolute",top:16,right:16,background:"none",border:"none",cursor:"pointer",color:"var(--c-text-3)",padding:4,borderRadius:8,lineHeight:1,display:"flex",zIndex:10}}
        >
          <X size={20}/>
        </button>

        {/* Skip link rendered top-right (#239 follow-up) — sits above the
            step's CTA so the mobile software keyboard can't hide it when
            an input has focus. Hidden on Reveal (no skip on the final
            step — the primary CTA closes the wizard). */}
        {currentKey !== "reveal" && (
          <div style={{position:"absolute", top:12, right:48, zIndex:1}}>
            <button
              type="button"
              aria-label="Skip onboarding for now"
              onClick={onDismiss}
              style={{
                background:"none", border:"none", cursor:"pointer",
                color:"var(--c-text-3)", fontSize:"var(--font-tiny)",
                fontFamily:"inherit", padding:"4px 8px",
                textDecoration:"underline",
              }}
            >
              Skip for now
            </button>
          </div>
        )}

        {currentKey === "welcome" && (
          <OnboardingWelcome data={onboardData} setData={setOnboardData} onContinue={goNext} step={safeStep} total={totalSteps} />
        )}
        {currentKey === "gardenSpaces" && (
          <OnboardingGardenSpaces data={onboardData} setData={setOnboardData} onContinue={goNext} step={safeStep} total={totalSteps} />
        )}
        {currentKey === "areas" && (
          <OnboardingAreas areas={areas} setAreas={setAreas} gardenSpaces={onboardData.gardenSpaces || []} onContinue={goNext} step={safeStep} total={totalSteps} />
        )}
        {currentKey.startsWith("sub:") && (
          <OnboardingSubSpaces
            key={currentKey} /* H-01: remount per area so chips/ticked state re-seeds */
            areaName={currentKey.slice(4)}
            areas={areas}
            setAreas={setAreas}
            onContinue={goNext}
            step={safeStep}
            total={totalSteps}
          />
        )}
        {currentKey === "plant" && (
          <OnboardingPlant
            plants={plants}
            areas={areas}
            newPlant={newPlant}
            setNewPlant={setNewPlant}
            onAddPlant={onAddPlant}
            features={features}
            aiReady={aiReady}
            aiStatus={aiStatus}
            onAiFailure={onAiFailure}
            onContinue={goNext}
            step={safeStep}
            total={totalSteps}
          />
        )}
        {currentKey === "reveal" && (
          <OnboardingTaskReveal
            plants={plants}
            setView={setView}
            onComplete={onComplete}
            step={safeStep}
            total={totalSteps}
            areas={areas}
          />
        )}

        {/* D-20 — Back button preserved. Visible N-of-M text is hidden on */}
        {/* non-Reveal screens because the sunrise rail (OnbProgressRail, */}
        {/* inside each OnbFrame) now carries progress with its own */}
        {/* aria-label="Step N of M". An sr-only span is retained here so */}
        {/* screen readers still announce step position via BOTH the rail */}
        {/* AND this text, and so tests can assert via aria-label. */}
        {/* Back button additionally hidden at step 0 so Welcome has no back */}
        {/* affordance (UAT gap 1). */}
        {currentKey !== "reveal" && safeStep > 0 && (
          <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:12, marginTop:24}}>
            <button
              type="button"
              aria-label="Go back to previous step"
              onClick={goBack}
              style={{
                display:"inline-flex", alignItems:"center", gap:4,
                background:"none", border:"none", cursor:"pointer",
                color:"var(--c-text-3)", fontSize:"var(--font-small)",
                fontFamily:"inherit", padding:"4px 8px"
              }}
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Back
            </button>
            {/* D-01/D-20 — step position is announced by OnbProgressRail's
                own aria-label="Step N of M" (see OnbProgressRail at line ~955).
                No duplicate sr-only span here — a second element with the
                same aria-label would cause RTL's getByLabelText to throw
                "Found multiple elements". Rewritten Phase 31.3 page-counter
                tests resolve the rail directly. */}
          </div>
        )}

        {/* Skip link previously rendered here at the bottom — moved to top-right
            above so the software keyboard doesn't hide it on mobile. */}
      </div>
    </div>
  );
}
